<template>
  <div style="margin: 0 20px 20px;">
    <FiPageRail title="Delivery Failures">
      <template #actions>
        <b-button size="sm" variant="success" @click="showInfoModal = true" aria-label="more info" title="more info"><i class="fa fa-info-circle"></i></b-button>
      </template>
    </FiPageRail>

    <div class="ad-header-calendar">
      <div class="mdash-range-group" role="group" aria-label="Delivery failures date range">
        <button
          v-for="option in dateRangeOptions"
          :key="option"
          type="button"
          class="mdash-range-btn"
          :class="{ 'mdash-range-btn--active': isActiveRange(option) }"
          :disabled="loading"
          @click="set_days_range(option)"
        >{{ option }}d</button>
      </div>
      <b-button-group class="toolbar-date-shifts">
        <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Shift back" v-on:click.prevent="shift_date(-selectedDayCount)"><i class="fa fa-angle-double-left" aria-hidden="true"></i></b-button>
        <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Previous day" v-on:click.prevent="shift_date(-1)"><i class="fa fa-angle-left" aria-hidden="true"></i></b-button>
      </b-button-group>
      <date-picker class="ad-header-calendar-input" v-model="details_fromDate" :config="options" :placeholder="details_fromDate"/>
      <span>to</span>
      <date-picker class="ad-header-calendar-input" v-model="details_toDate" :config="options" :placeholder="details_toDate"/>
      <b-button-group class="toolbar-date-shifts">
        <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Next day" v-on:click.prevent="shift_date(1)"><i class="fa fa-angle-right" aria-hidden="true"></i></b-button>
        <b-button size="sm" variant="outline-secondary" class="toolbar-date-shift-btn" title="Shift forward" v-on:click.prevent="shift_date(selectedDayCount)"><i class="fa fa-angle-double-right" aria-hidden="true"></i></b-button>
      </b-button-group>
      <b-button size="sm" v-on:click.prevent="load_data()">Search</b-button>
    </div>

    <b-modal v-model="showInfoModal" title="How Delivery Failures Works" size="lg" ok-only ok-title="Close">
      <div class="text-left" style="white-space: pre-line;">{{pageInfoText}}</div>
    </b-modal>

    <div v-if="loading" class="text-center" style="margin-top: 40px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <template v-if="!loading">
      <div v-if="!failedRecords.length" class="text-center" style="margin-top: 60px; color: #888;">
        <h4>No delivery failures found for this date range.</h4>
      </div>

      <template v-else>
        <!-- filter chips: click to toggle, counts are over the full unfiltered failure set -->
        <div class="df-filter-row">
          <span class="df-filter-label">Type:</span>
          <span
            v-for="et in errorTypeCounts"
            :key="'type-' + et.type"
            @click="toggleErrorType(et.type)"
            class="df-chip df-chip-clickable"
            :class="[typeChipClass(et.type), { 'df-chip-selected': selectedErrorTypes.includes(et.type) }]"
          >{{et.type}}: {{et.count}}</span>

          <span class="df-filter-label" style="margin-left: 10px;">Reason:</span>
          <span
            v-for="r in reasonCounts"
            :key="'reason-' + r.reason"
            @click="toggleReason(r.reason)"
            class="df-chip df-chip-clickable"
            :class="{ 'df-chip-selected': selectedReasons.includes(r.reason) }"
            :style="reasonChipStyle(r.reason)"
          >{{r.reason}}: {{r.count}}</span>
        </div>

        <b-table :items="filteredRecords" :fields="fields" bordered striped
                 :current-page="currentPage" :per-page="perPage">
          <template slot="errorTypeLabel" slot-scope="row">
            <span
              v-for="t in row.item.errorTypes"
              :key="t"
              class="df-mini-text"
              :style="typeTextStyle(t)"
            >{{t}}</span>
          </template>
          <template slot="reason" slot-scope="row">
            <span
              v-for="r in row.item.reasons"
              :key="r"
              class="df-mini-text"
              :style="reasonTextStyle(r)"
            >{{r}}</span>
          </template>
          <template slot="preview" slot-scope="row">
            <span class="df-preview">{{row.item.preview}}</span>
          </template>
        </b-table>
        <b-row>
          <b-col>
            <b-pagination
              v-model="currentPage"
              :total-rows="filteredRecords.length"
              :per-page="perPage"
              align="center"
            />
          </b-col>
        </b-row>
      </template>
    </template>
  </div>
</template>

<script>
import { get_alerts } from '../networking/apis'
import { mapState } from 'vuex'
import moment from 'moment'
import { justDate } from '@/helpers/dates'

// reason text is open-ended (varies by sms carrier), so colors are assigned by
// hashing the string into this fixed palette rather than a static per-value map.
// solid colors + white text, matching the tenant_manager chip style.
const REASON_COLOR_PALETTE = [
  '#0d6efd', '#20c997', '#6610f2', '#d63384',
  '#198754', '#0dcaf0', '#665191', '#b5511e',
]

// midnight reverses the reason chips: same hue, but as a low-alpha wash with a
// lightened ink instead of a solid fill with white text, matching the type
// chips in midnight.css and the rest of the chip family. index-parallel to
// REASON_COLOR_PALETTE -- keep the two in step. each ink was solved against its
// own composited wash rather than picked by eye, and clears 5.1-5.7:1.
// this has to live in js because the fill is an inline style (the hue is chosen
// by hashing the reason string), and inline styles beat any stylesheet.
const REASON_INK_MIDNIGHT = [
  '#5398fe', '#1fc291', '#ab7cf8', '#e26fa8',
  '#21b570', '#0cbbdf', '#a090c1', '#e07843',
]

const CHIP_WASH_ALPHA = 0.16

function hexWash(hex, alpha) {
  const n = parseInt(hex.slice(1), 16)
  return 'rgba(' + ((n >> 16) & 255) + ', ' + ((n >> 8) & 255) + ', ' + (n & 255) + ', ' + alpha + ')'
}

// quick date ranges in the header, matching the Alerts Dashboard
const DATE_RANGE_OPTIONS = [1, 7, 30, 90]

export default {
  name: 'delivery_failures',
  data() {
    return {
      loading: true,
      items_full: [],
      selectedErrorTypes: [],
      selectedReasons: [],
      currentPage: 1,
      perPage: 25,
      showInfoModal: false,
      pageInfoText: `This page only shows alerts that failed to deliver - either an SMS that was rejected by the carrier, or an email that bounced or was marked as a complaint.

1. Pick a date range (defaults to the last month) and click Search. The 1d/7d/30d/90d buttons and the arrows next to the dates reload on their own.

2. Use the Type chips to filter by delivery channel: SMS Failed, Email Bounced, or Email Complaint. Selecting more than one Type shows failures matching any of them.

3. Use the Reason chips to filter by the specific cause - the raw carrier response for SMS (varies by phone carrier), or the decoded email status. Reasons that mention "opted out" are grouped together regardless of which phone number opted out. Selecting more than one Reason shows failures matching any of them.

4. Type and Reason filters combine together: if you select a Type AND a Reason, only failures matching both show up.

5. The table below lists every matching failure with its booking, vehicle, recipient, and the message that failed to send.`,
      details_fromDate: justDate(moment().subtract(1, 'month')),
      details_toDate: justDate(moment()),
      options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(360, 'days'),
        maxDate: moment(),
        keepOpen: false,
        showClear: true,
        showClose: true,
        toolbarPlacement: 'top',
      },
      fields: [
        { key: 'timeLabel', label: 'Time', sortable: true },
        { key: 'alert_type', label: 'Alert Type', sortable: true },
        { key: 'booking_id', label: 'Booking ID', sortable: true },
        { key: 'vehicle_id', label: 'Vehicle ID', sortable: true },
        { key: 'recipient', label: 'Recipient', sortable: true },
        { key: 'contact', label: 'Phone / Email', sortable: true },
        { key: 'errorTypeLabel', label: 'Error Type', sortable: true },
        { key: 'reason', label: 'Reason', sortable: false },
        { key: 'preview', label: 'Message Preview', sortable: false },
      ],
    }
  },
  async created() {
    this.load_data()
  },
  methods: {
    async load_data() {
      this.loading = true
      try {
        const { value: { data = [] } } = await get_alerts(
          this.user.tenant,
          '',
          moment(this.details_fromDate, 'MM-DD-YYYY').format('YYYY-MM-DD'),
          moment(this.details_toDate, 'MM-DD-YYYY').format('YYYY-MM-DD')
        )
        if (data.summary && data.summary.timezone) moment.tz.setDefault(data.summary.timezone)
        this.items_full = data.alerts || []
      } catch (e) {
        this.$emit('showAlert', 'error retrieving data', 'danger', 5000)
      }
      this.currentPage = 1
      this.loading = false
    },
    shift_date(shift) {
      this.details_fromDate = moment(this.details_fromDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
      this.details_toDate = moment(this.details_toDate, 'MM-DD-YYYY').add(shift, 'days').format('MM-DD-YYYY')
      this.load_data()
    },
    set_days_range(option) {
      this.details_fromDate = justDate(moment().subtract(option, 'days'))
      this.details_toDate = justDate(moment())
      this.load_data()
    },
    // active state is derived from the current from/to dates rather than a
    // separately tracked "selected option", so it stays correct whether the
    // range came from a quick button, the date pickers, or the shift arrows
    isActiveRange(option) {
      return this.details_fromDate === justDate(moment().subtract(option, 'days')) &&
        this.details_toDate === justDate(moment())
    },
    // mirrors the "Problems Only" definition on the Alert History page, so a
    // delivery counts as failed the same way everywhere.
    getLatestEmailStatus(item) {
      if (!item.email_msg_status || !item.email_msg_status.length) return null
      return item.email_msg_status.reduce((a, b) => (new Date(a.datetime) > new Date(b.datetime) ? a : b))
    },
    isAlertFailed(item) {
      const emailStatus = this.getLatestEmailStatus(item)
      const hasEmailIssue = emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase())
      const hasSmsIssue = item.provider_response && !item.provider_response.includes('Message has been accepted')
      return !!(hasEmailIssue || hasSmsIssue)
    },
    // the distinct, filterable error categories an item can match - an item can
    // match more than one (e.g. both its sms and email attempts failed)
    errorTypesFor(item) {
      const types = []
      if (item.provider_response && !item.provider_response.includes('Message has been accepted')) {
        types.push('SMS Failed')
      }
      const emailStatus = this.getLatestEmailStatus(item)
      if (emailStatus) {
        const status = emailStatus.status.toLowerCase()
        if (status === 'bounce') types.push('Email Bounced')
        if (status === 'complaint') types.push('Email Complaint')
      }
      return types
    },
    // per-type color, tenant_manager-style (solid color + white text), so each
    // error type reads at a glance
    typeChipClass(type) {
      const map = {
        'SMS Failed': 'df-type-sms-failed',
        'Email Bounced': 'df-type-email-bounced',
        'Email Complaint': 'df-type-email-complaint',
      }
      return map[type] || ''
    },
    // same per-type colors as the filter chip, but as bold text for the results
    // table. midnight takes the lightened variants: the solid hues are built for
    // white and land at 3.43:1 (bounced) and 2.38:1 (complaint) on the dark table.
    typeTextStyle(type) {
      const map = this.midnight ? {
        'SMS Failed': '#fd7707',
        'Email Bounced': '#e6707b',
        'Email Complaint': '#a78cd9',
      } : {
        'SMS Failed': '#fd7e14',
        'Email Bounced': '#dc3545',
        'Email Complaint': '#6f42c1',
      }
      return { color: map[type] || 'var(--fi-text)', fontWeight: 'bold' }
    },
    // deterministic so a given reason string always gets the same color,
    // regardless of where it falls in the (count-sorted, so unstable) chip order
    hashString(str) {
      let hash = 0
      for (let i = 0; i < str.length; i++) {
        hash = (hash * 31 + str.charCodeAt(i)) | 0
      }
      return Math.abs(hash)
    },
    reasonChipStyle(reason) {
      const i = this.hashString(reason) % REASON_COLOR_PALETTE.length
      const hue = REASON_COLOR_PALETTE[i]
      if (this.midnight) {
        return { backgroundColor: hexWash(hue, CHIP_WASH_ALPHA), borderColor: hue, color: REASON_INK_MIDNIGHT[i] }
      }
      return { backgroundColor: hue, borderColor: hue, color: '#fff' }
    },
    // same per-reason color as the filter chip, but as bold text for the results
    // table. six of the eight raw hues fall between 2.16:1 and 3.45:1 on the dark
    // table, so midnight uses the lightened set (5.1-6.8:1).
    reasonTextStyle(reason) {
      const i = this.hashString(reason) % REASON_COLOR_PALETTE.length
      return { color: this.midnight ? REASON_INK_MIDNIGHT[i] : REASON_COLOR_PALETTE[i], fontWeight: 'bold' }
    },
    // distinct reason strings an item can match - raw sms provider response text
    // (varies by carrier) plus the decoded email status, so the reason chips are
    // more specific than the broad error-type chips above
    reasonsFor(item) {
      const reasons = []
      if (item.provider_response && !item.provider_response.includes('Message has been accepted')) {
        reasons.push(this.normalizeSmsReason(item.provider_response))
      }
      const emailStatus = this.getLatestEmailStatus(item)
      if (emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase())) {
        reasons.push(this.decodeEmailStatus(emailStatus.status))
      }
      return reasons
    },
    // "+12125864095 is opted out" varies per phone number, which would otherwise
    // fragment the opt-out cases into one chip per number - strip the number so
    // they all group under a single "opted out" chip
    normalizeSmsReason(raw) {
      const text = raw.trim()
      if (/opted out/i.test(text)) return 'opted out'
      return text
    },
    decodeEmailStatus(rawStatus) {
      const map = {
        send: 'sent',
        delivery: 'delivered',
        bounce: 'bounced',
        complaint: 'filed complaint',
        open: 'opened',
        click: 'clicked link',
      }
      return map[(rawStatus || '').toLowerCase()] || rawStatus
    },
    failureReason(item) {
      const parts = []
      if (item.provider_response && !item.provider_response.includes('Message has been accepted')) {
        parts.push(`sms: ${item.provider_response}`)
      }
      const emailStatus = this.getLatestEmailStatus(item)
      if (emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase())) {
        parts.push(`email: ${this.decodeEmailStatus(emailStatus.status)}`)
      }
      return parts.join(' | ') || 'unknown'
    },
    // the phone number and/or email address that actually bounced/failed
    failureContact(item) {
      const parts = []
      if (item.provider_response && !item.provider_response.includes('Message has been accepted')) {
        // driver/leader recipients live in sms_drivers/sms_leaders rather than
        // the plain sms field, so sms_all_recipients is the one that's actually
        // populated for the driver-facing alert types (LateAlert, MissingDVIR, ...)
        const phone = item.sms_all_recipients || item.sms || item.sms_drivers || item.sms_leaders
        if (phone) parts.push(phone)
      }
      const emailStatus = this.getLatestEmailStatus(item)
      if (emailStatus && ['bounce', 'complaint'].includes(emailStatus.status.toLowerCase()) && item.email) {
        parts.push(item.email)
      }
      return parts.join(' | ')
    },
    firstLine(item) {
      const text = item.message || item.email_message || item.email_subject || ''
      const line = String(text).split('\n')[0].trim()
      return line.length > 80 ? line.slice(0, 80) + '…' : line
    },
    toggleErrorType(type) {
      const idx = this.selectedErrorTypes.indexOf(type)
      if (idx > -1) this.selectedErrorTypes.splice(idx, 1)
      else this.selectedErrorTypes.push(type)
      this.currentPage = 1
    },
    toggleReason(reason) {
      const idx = this.selectedReasons.indexOf(reason)
      if (idx > -1) this.selectedReasons.splice(idx, 1)
      else this.selectedReasons.push(reason)
      this.currentPage = 1
    },
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    // the chip fills and the table's per-type / per-reason inks are inline
    // styles, so they cannot be reached from midnight.css and have to branch here
    midnight() {
      return this.$store.state.profile.site_theme === 'midnight'
    },
    dateRangeOptions() {
      return DATE_RANGE_OPTIONS
    },
    // length of the current from/to window in days, so the double-arrow
    // shift moves by "one more of these" instead of a fixed step
    selectedDayCount() {
      const diff = moment(this.details_toDate, 'MM-DD-YYYY').diff(moment(this.details_fromDate, 'MM-DD-YYYY'), 'days')
      return diff > 0 ? diff : 1
    },
    failedRecords() {
      return this.items_full
        .filter(item => this.isAlertFailed(item))
        .map(item => {
          const errorTypes = this.errorTypesFor(item)
          return {
            key: item.movement_vehicle_id || `${item.alert_type}-${item.processed_time}`,
            processed_time: item.processed_time,
            timeLabel: moment(item.processed_time).format('MM/DD/YY h:mma'),
            alert_type: item.alert_type,
            booking_id: item.booking_id || '',
            vehicle_id: item.vehicle_id || item.movement_vehicle_id || '',
            recipient: item.sms_name || '',
            contact: this.failureContact(item),
            errorTypes,
            errorTypeLabel: errorTypes.join(', '),
            reasons: this.reasonsFor(item),
            reason: this.failureReason(item),
            preview: this.firstLine(item),
          }
        })
        .sort((a, b) => new Date(b.processed_time) - new Date(a.processed_time))
    },
    errorTypeCounts() {
      const counts = {}
      this.failedRecords.forEach(r => {
        r.errorTypes.forEach(t => { counts[t] = (counts[t] || 0) + 1 })
      })
      return Object.entries(counts)
        .map(([type, count]) => ({ type, count }))
        .sort((a, b) => b.count - a.count)
    },
    reasonCounts() {
      const counts = {}
      this.failedRecords.forEach(r => {
        r.reasons.forEach(reason => { counts[reason] = (counts[reason] || 0) + 1 })
      })
      return Object.entries(counts)
        .map(([reason, count]) => ({ reason, count }))
        .sort((a, b) => b.count - a.count)
    },
    filteredRecords() {
      let recs = this.failedRecords
      if (this.selectedErrorTypes.length) {
        recs = recs.filter(r => r.errorTypes.some(t => this.selectedErrorTypes.includes(t)))
      }
      if (this.selectedReasons.length) {
        recs = recs.filter(r => r.reasons.some(reason => this.selectedReasons.includes(reason)))
      }
      return recs
    },
  },
  watch: {
    user: {
      deep: true,
      handler() {
        this.load_data()
      },
    },
  },
}
</script>

<style scoped>
/* date controls mirror the Alerts Dashboard / Alert History header: quick
   ranges, shift arrows, compact pickers — same sizes across the three pages */
.ad-header-calendar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  font-size: 13px;
}

.ad-header-calendar-input {
  font-size: 13px;
  font-weight: bold;
  width: 100px;
}

.toolbar-date-shift-btn {
  padding: 0.15rem 0.35rem;
  line-height: 1;
  min-width: 1.65rem;
}

.toolbar-date-shift-btn .fa {
  font-size: 13px;
  font-weight: bold;
  vertical-align: middle;
}

.mdash-range-group {
  display: inline-flex;
  border: 1px solid var(--fi-line-strong);
  border-radius: var(--fi-radius-sm);
  overflow: hidden;
}

.mdash-range-btn {
  background: var(--fi-surface);
  color: var(--fi-text-muted);
  border: none;
  border-left: 1px solid var(--fi-line-strong);
  padding: 5px 12px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}

.mdash-range-btn:first-child {
  border-left: none;
}

.mdash-range-btn:hover:not(:disabled) {
  background: var(--fi-surface-sunken);
  color: var(--fi-text);
}

.mdash-range-btn:disabled {
  cursor: default;
  opacity: 0.6;
}

.mdash-range-btn--active,
.mdash-range-btn--active:hover:not(:disabled) {
  background: var(--fi-brand);
  color: var(--fi-brand-contrast);
}

/* below this width the absolutely-positioned title/button would overlap the
   centered date-nav content as it wraps, so stack everything into its own
   full-width line instead */
@media (max-width: 768px) {
  .ad-header-left,
  .ad-header-right,
  .ad-header-calendar {
    position: static;
    transform: none;
    width: 100%;
    justify-content: center;
    margin: 6px 0;
  }
}

.df-preview {
  color: #555;
  font-style: italic;
  font-size: 13px;
}

.df-mini-text {
  display: inline-block;
  margin: 2px 6px 2px 0;
  font-size: 13px;
}

/* chip look matches tenant_manager.vue's .tenant-chip / .tenant-filter-clickable */
.df-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 6px 8px;
  margin: 10px 10px 16px;
  font-size: 14px;
}

.df-filter-label {
  font-weight: bold;
  margin-right: 4px;
  white-space: nowrap;
}

.df-chip {
  display: inline-block;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 3px 10px;
  white-space: nowrap;
  line-height: 1.3;
  font-weight: bold;
  background-color: #f8f9fa;
  color: #212529;
}

.df-chip-clickable {
  cursor: pointer;
}

.df-chip-clickable:hover {
  filter: brightness(0.95);
}

.df-chip-selected {
  border: 2px solid #dc3545 !important;
  box-shadow: 0 0 0 1px rgba(220, 53, 69, 0.2);
  padding: 2px 9px;
}

/* type chip colors, solid + white text, matching tenant_manager's semantic chips */
.df-chip.df-type-sms-failed { background-color: #fd7e14; border-color: #fd7e14; color: #fff; }
.df-chip.df-type-email-bounced { background-color: #dc3545; border-color: #dc3545; color: #fff; }
.df-chip.df-type-email-complaint { background-color: #6f42c1; border-color: #6f42c1; color: #fff; }
</style>
